import { ReactNode, useState, useCallback } from "react";
import { Snackbar, Box } from "@mui/joy";
import { NotificationContext, Notification, NotificationType } from "../context/NotificationContext";

interface NotificationProviderProps {
    children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const showNotification = useCallback(
        (message: string, type: NotificationType = "neutral", duration: number = 3000) => {
            const id = `notification-${Date.now()}-${Math.random()}`;
            const notification: Notification = { id, message, type, duration };

            setNotifications((prev) => [...prev, notification]);

            // Auto remove after duration
            setTimeout(() => {
                setNotifications((prev) => prev.filter((n) => n.id !== id));
            }, duration);
        },
        []
    );

    const showSuccess = useCallback(
        (message: string, duration?: number) => {
            showNotification(message, "success", duration);
        },
        [showNotification]
    );

    const showError = useCallback(
        (message: string, duration?: number) => {
            showNotification(message, "danger", duration);
        },
        [showNotification]
    );

    const showWarning = useCallback(
        (message: string, duration?: number) => {
            showNotification(message, "warning", duration);
        },
        [showNotification]
    );

    const showInfo = useCallback(
        (message: string, duration?: number) => {
            showNotification(message, "neutral", duration);
        },
        [showNotification]
    );

    const removeNotification = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const contextValue = {
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
    };

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
            {/* Render snackbars */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 16,
                    right: 16,
                    zIndex: 10000,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxWidth: "400px",
                }}
            >
                {notifications.map((notification, index) => (
                    <Snackbar
                        key={notification.id}
                        open={true}
                        variant="soft"
                        color={notification.type}
                        autoHideDuration={notification.duration}
                        onClose={() => removeNotification(notification.id)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        sx={{
                            transform: `translateY(${index * -8}px)`,
                        }}
                    >
                        {notification.message}
                    </Snackbar>
                ))}
            </Box>
        </NotificationContext.Provider>
    );
};
